import { t } from "i18next";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const minPasswordLength = 8;

export const UserSchema = z
    .object({
        id: z.int().positive(),
        firstName: z.string().trim().default(""),
        lastName: z.string().trim().default(""),
        password: z.string().trim().min(minPasswordLength).optional(),
        email: z.string().trim(),
    })
    .readonly()
    .brand<"User">();

export type User = z.output<typeof UserSchema>;

export const UserResumeSchema = z
    .object({
        id: z.int().positive(),
        email: z.string().trim(),
    })
    .readonly()
    .brand<"UserResume">();

export type UserResume = z.output<typeof UserResumeSchema>;

export const UserCreateSchema = z
    .object({
        email: z.email(),
        password: z.string().trim().min(minPasswordLength),
        fieldTech: z.boolean().default(false),
    })
    .readonly()
    .brand<"UserCreate">();

export type UserCreate = z.output<typeof UserCreateSchema>;

export const UserEditSchema = z
    .object({
        id: z.int().positive(),
        email: z.email(),
        password: z.string().trim().min(minPasswordLength).optional(),
        fieldTech: z.boolean().default(false),
    })
    .readonly()
    .brand<"UserEdit">();

export type UserEdit = z.output<typeof UserEditSchema>;

const usersSchema = z.object({
    items: z.array(UserSchema),
    count: z.int().min(0),
});

export type UsersInputProps = z.input<typeof usersSchema>;
export type UsersProps = z.output<typeof usersSchema>;

export class Users {
    public readonly __brand = "Users";

    get items(): readonly User[] {
        return this.props.items;
    }

    get count(): number {
        return this.props.count;
    }

    private constructor(private readonly props: Readonly<UsersProps>) {}

    static empty(): Users {
        return Users.create({
            items: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: UsersInputProps,
    ): Result<UsersProps, z.ZodError<UsersProps>> {
        const result = usersSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<UsersInputProps>,
    ): Result<Users, z.ZodError<UsersProps>> {
        const validateResult = Users.validate(props);

        return validateResult.map(
            (validatedProps) => new Users(validatedProps),
        );
    }
}

export const UserCreateToSpatialLevelSchema = z.object({
    email: z.email(),
    username: z
        .string()
        .trim()
        .optional()
        .refine((val) => val === undefined || val === "" || !/\s/.test(val), {
            error: () => t("validation:models.user.usernameValidation"),
        }),
    spatialLevelIds: z.array(z.number()),
    fieldTech: z.boolean().default(false),
});

export type UserCreateToSpatialLevel = z.output<
    typeof UserCreateToSpatialLevelSchema
>;
